import { faArrowRightToBracket, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import icon from 'assets/login-page-icon.png';
import { Button } from 'components/button';
import { GeneralInput } from 'components/input';
import { LanguageSwitcher } from 'components/language-switcher';
import { useDispatch, useLanguageNavigate, useLocale, useSelector } from 'hooks';
import get from 'lodash/get';
import Giver from 'models/giver';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { authLogin } from '@/redux/actions';
import { resetErrors } from '@/redux/actions_ui';
import * as cons from '@/redux/constants';
import { useI18n } from 'locales';

const selector = (state) => ({
  authLoginInfo: state.authLoginInfo,
});

function HomePage() {
  const { control, formState: { errors }, handleSubmit, resetField, clearErrors } = useForm({
    reValidateMode: 'onBlur',
  });
  const navigate = useLanguageNavigate();
  const locale = useLocale();
  const [showVerificationCodeBox, setShowVerificationCodeBox] = useState(false);
  const { authLoginInfo } = useSelector(selector);
  const dispatch = useDispatch();
  const t = useI18n();

  useEffect(() => {
    switch (authLoginInfo.type) {
      case cons.AUTH_LOGIN.SUCCESS:
        dispatch(resetErrors());
        navigate('/dashboard');
        break;
      case cons.AUTH_LOGIN.FAILURE:
        if (
          get(authLoginInfo, 'error.error.name') === 'UNAUTHORIZED' &&
          get(authLoginInfo, 'error.error.description') === 'Verification code required'
        ) {
          setShowVerificationCodeBox(true);
        } else {
          resetField('code');
          setShowVerificationCodeBox(false);
        }
        break;
      default:
        break;
    }
  }, [authLoginInfo, navigate, setShowVerificationCodeBox, resetField, dispatch]);

  const onSubmit = useCallback(
    (data, e) => {
      e.preventDefault();
      dispatch(authLogin({ payload: data }));
    },
    [dispatch]
  );
  const onError = useCallback(
    (errors) => {
      if (!errors.email && !errors.password && errors.code) {
        if (!showVerificationCodeBox) {
          clearErrors('code');
          setShowVerificationCodeBox(true);
        }
      }
    },
    [clearErrors, showVerificationCodeBox, setShowVerificationCodeBox]
  );
  const isFetching = authLoginInfo.type === cons.AUTH_LOGIN.FETCHING || authLoginInfo.type === cons.AUTH_LOGIN.SUCCESS;

  const [isMobile, setIsMobile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Stack gap={4} sx={{
      backgroundColor: 'background.default',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      position: 'relative',
    }}>
      <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', mt: -2, px: 2 }}>
        <LanguageSwitcher />
      </Stack>

      <Stack gap={2} sx={{ alignItems: 'center', display: 'flex', width: '280px' }}>
        <img src={icon} alt='icon' style={{ width: '100px', height: '100px' }} />

        <Typography variant='h5' color='secondary.main' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('GIVING')}
        </Typography>
      </Stack>

      <Stack gap={1} sx={{ alignItems: 'center', display: 'flex', width: isMobile ? '90%' : '400px' }}>
        {
          get(authLoginInfo, 'error.error') && (
            <Alert severity="error">
              {
                get(authLoginInfo, 'error.status') === 'Pending' ? (
                  <>
                    {t('Your account is pending verification. Please check your email for the verification code and')} <Link href={`/giving/register/verify?email=${get(authLoginInfo, 'error.email')}&locale=${locale}`} className="underline">{t('enter it in this page')}</Link>.
                  </>
                ) : (
                  get(authLoginInfo, 'error.error.description') || get(authLoginInfo, 'error.error')
                )
              }
            </Alert>
          )
        }

        <form onSubmit={handleSubmit(onSubmit, onError)} className="w-full">
          <Stack direction='column' gap={2}>
            <Box hidden={showVerificationCodeBox}>
              <Stack direction='column' gap={2}>
                <GeneralInput
                  name='email'
                  type='email'
                  control={control}
                  registerOptions={Giver.validationRules.email}
                  label={t('Email')}
                  readOnly={isFetching}
                  error={errors.email}
                  helper={get(errors, 'email.message')}
                />

                <GeneralInput
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  control={control}
                  registerOptions={Giver.validationRules.password}
                  label={t('Password')}
                  readOnly={isFetching}
                  error={errors.password}
                  helper={get(errors, 'password.message')}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((v) => !v)}
                        edge="end"
                        size="small"
                        aria-label={showPassword ? t('Hide password') : t('Show password')}
                      >
                        <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} style={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </Stack>
            </Box>

            <Box hidden={!showVerificationCodeBox}>
              <GeneralInput
                name='code'
                type='text'
                control={control}
                registerOptions={Giver.validationRules.code}
                inputProps={{ maxLength: 6 }}
                label={t('Enter the 6-digit code')}
                readOnly={isFetching}
                error={errors.code}
                helper={get(errors, 'code.message')}
              />
            </Box>

            <Button
              variant="contained"
              type="submit"
              loading={isFetching}
              loadingPosition='start'
              size="large"
              leading={faArrowRightToBracket}
              fullWidth
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }}
            >
              {t('Login')}
            </Button>
          </Stack>
        </form>

        <Stack direction={isMobile ? 'column' : 'row'} gap={2} sx={{ width: '100%', mt: 1, alignItems: isMobile ? 'center' : 'flex-start', justifyContent: isMobile ? 'center' : 'space-between' }}>
          <Typography variant='body2'>
            <Link href={`/giving/register?locale=${locale}`} color="secondary.main" underline="always">
              {t('Create an account')}
            </Link>
          </Typography>

          <Link href={`/giving/forgot-password?locale=${locale}`} color="secondary.main" underline="always" variant="body2">
            {t('Forgot Password?')}
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default HomePage;
